import React, { useState } from "react";
import { Button, Col, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { BsFillInfoCircleFill } from "react-icons/bs";

import { setIsContactDefaultMappingsSaved } from "../../store/slice/contactSlice";
import { getUrl } from "../../helpers/url";
import { useCmsHook } from "../../helpers/cms/cmsHook";

import FieldsDropdown from "../FieldsDropdown";

function DefaultMappings() {
  const { cmsInstance } = useCmsHook();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const {
    defaultMappingFields: {
      defaultMappings: {
        paymentTerms,
        currencies,
        customerPostingGroups,
        vatPostingGroups,
        generalBusPostingGroups,
      },
      loading,
    },
    paymentTermsCodePreference,
    currencyCodePreference,
    customerPostingGroupPreference,
    vatBusinessPostingGroupPreference,
    generalBusinessPostingGroupPreference,
  } = useSelector((state) => state.contact);

  const [isLoading, setIsLoading] = useState(false);

  return (
    <div>
      <hr className="settings-horizontal-line" />

      {loading ? (
        <div
          className="d-flex flex-row justify-content-center align-items-center my-3"
          style={{ height: "40vh" }}
        >
          <Spinner
            animation="border"
            variant="secondary"
            role="status"
            size="md"
          ></Spinner>
        </div>
      ) : (
        <div>
          <div className="mt-3 ms-4">
            {cmsInstance &&
              cmsInstance.showRichText(
                "cmsContent.contact.defaultMappings",
                "rules-description"
              )}
            <div>
              <Col sm={6} lg={3} className="mt-3">
                <span className="rules-sub-headings">
                  Payment Terms Code{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip style={{ fontSize: "12px" }}>
                        This field indicates the terms of payment for a customer
                        (e.g., net 30 days, upon receipt, etc.). Select a
                        default code that will be applied to all new customers
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </span>
                {paymentTerms && paymentTerms.length > 0 && (
                  <FieldsDropdown
                    fieldsData={paymentTerms}
                    selectedPreference={paymentTermsCodePreference}
                    dropdownFor={"paymentTermsCode"}
                  />
                )}
              </Col>
            </div>

            <div>
              <Col sm={6} lg={3} className="mt-3">
                <span className="rules-sub-headings">
                  Currency Code{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip style={{ fontSize: "12px" }}>
                        Define the default currency to be used when a new
                        customer is created. This will determine the currency in
                        which all transactions with the customer will be
                        conducted
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </span>
                {currencies && currencies.length > 0 && (
                  <FieldsDropdown
                    fieldsData={currencies}
                    selectedPreference={currencyCodePreference}
                    dropdownFor={"currencyCode"}
                  />
                )}
              </Col>
            </div>

            <div>
              <Col sm={6} lg={3} className="mt-3">
                <span className="rules-sub-headings">
                  Customer Posting Group{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip style={{ fontSize: "12px" }}>
                        This field groups customers together to manage how
                        transactions are posted to your general ledger. Choose a
                        default group for new customers
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </span>
                {customerPostingGroups && customerPostingGroups.length > 0 && (
                  <FieldsDropdown
                    fieldsData={customerPostingGroups}
                    selectedPreference={customerPostingGroupPreference}
                    dropdownFor={"customerPostingGroup"}
                  />
                )}
              </Col>
            </div>

            <div>
              <Col sm={6} lg={3} className="mt-3">
                <span className="rules-sub-headings">
                  VAT Business Posting Group{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip style={{ fontSize: "12px" }}>
                        This field is used for managing VAT treatment in
                        Business Central. The VAT business posting group you
                        select here will be set as the default for all new
                        customers
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </span>
                {vatPostingGroups && vatPostingGroups.length > 0 && (
                  <FieldsDropdown
                    fieldsData={vatPostingGroups}
                    selectedPreference={vatBusinessPostingGroupPreference}
                    dropdownFor={"vatBusinessPostingGroup"}
                  />
                )}
              </Col>
            </div>

            <div>
              <Col sm={6} lg={3} className="mt-3">
                <span className="rules-sub-headings">
                  General Business Posting Group{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip style={{ fontSize: "12px" }}>
                        This field is also used to manage how transactions are
                        posted to the general ledger, but on a broader scale
                        than the Customer Posting Group. Select a default group
                        for new customers
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </span>
                {generalBusPostingGroups &&
                  generalBusPostingGroups.length > 0 && (
                    <FieldsDropdown
                      fieldsData={generalBusPostingGroups}
                      selectedPreference={generalBusinessPostingGroupPreference}
                      dropdownFor={"generalBusinessPostingGroup"}
                    />
                  )}
              </Col>
            </div>
          </div>

          <div className="save-button-container mt-5">
            {!paymentTermsCodePreference ||
            !currencyCodePreference ||
            !customerPostingGroupPreference ||
            !vatBusinessPostingGroupPreference ||
            !generalBusinessPostingGroupPreference ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip style={{ fontSize: "11px" }}>
                    {showToolTipText(
                      paymentTermsCodePreference,
                      currencyCodePreference,
                      customerPostingGroupPreference,
                      vatBusinessPostingGroupPreference,
                      generalBusinessPostingGroupPreference
                    )}
                  </Tooltip>
                }
              >
                <Button
                  className="save-changes-button"
                  style={{ opacity: "0.9", cursor: "not-allowed" }}
                >
                  Save
                </Button>
              </OverlayTrigger>
            ) : (
              <Button
                className="save-changes-button"
                onClick={() =>
                  onSaveContactDefaultMappings({
                    setIsLoading,
                    searchParams,
                    paymentTermsCodePreference,
                    currencyCodePreference,
                    customerPostingGroupPreference,
                    vatBusinessPostingGroupPreference,
                    generalBusinessPostingGroupPreference,
                    dispatch,
                    cmsInstance,
                  })
                }
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <span>Save</span>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DefaultMappings;

/**
 * This function is used for saving contact default mappings
 */
export async function onSaveContactDefaultMappings({
  setIsLoading,
  searchParams,
  paymentTermsCodePreference,
  currencyCodePreference,
  customerPostingGroupPreference,
  vatBusinessPostingGroupPreference,
  generalBusinessPostingGroupPreference,
  dispatch,
  cmsInstance,
}) {
  try {
    setIsLoading(true);
    const account = searchParams.get("account");
    await axios.post(getUrl("REACT_APP_SAVE_PREFERENCES"), {
      account,
      preferences: {
        stripeFields: {
          paymentTerms: paymentTermsCodePreference,
          currency: currencyCodePreference,
          customerPostingGroup: customerPostingGroupPreference,
          vatBusinessPostingGroup: vatBusinessPostingGroupPreference,
          generalBusinessPostingGroup: generalBusinessPostingGroupPreference,
        },
      },
      preferenceType: "customer",
    });
    dispatch(setIsContactDefaultMappingsSaved());
    toast.success(
      cmsInstance &&
        cmsInstance.sendData().cmsContent.notifications.syncSettings
          .successContent.contactDefaultMappings.message
    );
  } catch (error) {
    console.log(error);
    let errorMessage;
    if (error.response && error.response.data.message)
      errorMessage = error.response.data.message;
    else errorMessage = error.message;
    toast.error(errorMessage);
  } finally {
    setIsLoading(false);
  }
}

/**
 * This function is used for showing custom message in Tooltip for save button
 * @param {Object} paymentTermsCodePreference selected Payment terms code
 * @returns {String} custom message
 */
const showToolTipText = (
  paymentTermsCodePreference,
  currencyCodePreference,
  customerPostingGroupPreference,
  vatBusinessPostingGroupPreference,
  generalBusinessPostingGroupPreference
) => {
  if (!paymentTermsCodePreference) {
    return "Please select payment terms code.";
  } else if (!currencyCodePreference) {
    return "Please select currency code.";
  } else if (!customerPostingGroupPreference) {
    return "Please select customer posting group.";
  } else if (!vatBusinessPostingGroupPreference) {
    return "Please select VAT business posting group.";
  } else if (!generalBusinessPostingGroupPreference) {
    return "Please select general business posting group.";
  }
};
